import { message } from 'antd'
import { ArgsProps, MessageInstance } from 'antd/es/message/interface'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

type MessageProps = Partial<Omit<ArgsProps, 'content'>>

export type MessageFunction = (content: string, args?: ArgsProps) => void

export type GeckoMessageContextType = {
  messageApi: MessageInstance
  successMessage: MessageFunction
  errorMessage: MessageFunction
  infoMessage: MessageFunction
  warningMessage: MessageFunction
}

const DEFAULT_MESSAGE_PROPS: MessageProps = {
  duration: 3,
}

const GeckoMessageContext = createContext<GeckoMessageContextType | null>(null)

export const useGeckoMessages: () => GeckoMessageContextType = () => {
  const context = useContext(GeckoMessageContext)
  if (!context || context.messageApi === null) {
    throw new Error(
      'useGeckoMessages must be used within a GeckoMessageProvider'
    )
  }
  return context
}

export const GeckoMessageProvider: FC<{
  children: ReactNode
  messageProps?: MessageProps
}> = ({ children, messageProps }) => {
  const [api, contextHolder] = message.useMessage()

  const props = useMemo(
    () => messageProps ?? DEFAULT_MESSAGE_PROPS,
    [messageProps]
  )

  const successMessage = useCallback(
    (content: string, overrideProps?: MessageProps) =>
      api.success({
        content,
        ...(overrideProps ?? props),
      }),
    [api, props]
  )

  const errorMessage = useCallback(
    (content: string, overrideProps?: MessageProps) => {
      console.error(content)
      api.error({
        content,
        ...(overrideProps ?? props),
      })
    },
    [api, props]
  )

  const infoMessage = useCallback(
    (content: string, overrideProps?: MessageProps) =>
      api.info({
        content,
        ...(overrideProps ?? props),
      }),
    [api, props]
  )

  const warningMessage = useCallback(
    (content: string, overrideProps?: MessageProps) =>
      api.warning({
        content,
        ...(overrideProps ?? props),
      }),
    [api, props]
  )

  return (
    <GeckoMessageContext.Provider
      value={{
        messageApi: api,
        successMessage,
        errorMessage,
        infoMessage,
        warningMessage,
      }}>
      {contextHolder}
      {children}
    </GeckoMessageContext.Provider>
  )
}
